// Layout principal : sidebar desktop + navigation basse mobile
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  LayoutDashboard, CalendarDays, SquareKanban, ListChecks, Megaphone,
  ShieldCheck, Sun, Moon, LogOut, MoreHorizontal, Mail, HandHelping, FolderOpen, CalendarClock, Users,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { ROLES } from "@/lib/constants";
import { joursAvantSpectacle } from "@/lib/dates";
import { peutComposer } from "@/pages/Messages";

const NAV = [
  { to: "/dashboard", label: "Accueil", Icone: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/calendrier", label: "Calendrier éditorial", labelCourt: "Calendrier", Icone: CalendarDays, testId: "nav-calendrier" },
  { to: "/projets", label: "Projets", Icone: SquareKanban, testId: "nav-projets" },
  { to: "/taches", label: "Tâches", Icone: ListChecks, testId: "nav-taches" },
];

const NAV_SECONDAIRE = [
  { to: "/agenda", label: "Agenda", Icone: CalendarClock, testId: "nav-agenda" },
  { to: "/equipe", label: "L'équipe", Icone: Users, testId: "nav-equipe" },
  { to: "/ressources", label: "Ressources", Icone: FolderOpen, testId: "nav-ressources" },
];

const NAV_ADMIN = [
  { to: "/annonces", label: "Annonces", Icone: Megaphone, testId: "nav-annonces" },
  { to: "/admin", label: "Administration", Icone: ShieldCheck, testId: "nav-admin" },
];

const lienClasse = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? "bg-primary text-primary-foreground shadow-md"
      : "text-foreground/70 hover:bg-muted hover:text-foreground"
  }`;

const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  return (
    <button
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="p-2 rounded-full hover:bg-muted transition-colors"
      aria-label="Basculer le thème"
      data-testid="theme-toggle"
    >
      <Sun className="h-4 w-4 hidden dark:block" />
      <Moon className="h-4 w-4 dark:hidden" />
    </button>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const estAdmin = user?.role === "admin";
  const accesComposeur = peutComposer(user);

  const NAV_COMMUNICATION = [
    { to: "/demande-media", label: "Demande d'aide média", labelCourt: "Demande", Icone: HandHelping, testId: "nav-demande-media" },
  ];
  if (accesComposeur) {
    NAV_COMMUNICATION.push({ to: "/messages", label: "Messages", Icone: Mail, testId: "nav-messages" });
  }

  const deconnexion = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      {/* ——— Sidebar desktop ——— */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-card border-r border-border z-40">
        <div className="px-6 pt-8 pb-6">
          <h1 className="font-serif text-3xl font-bold tracking-tight">Inyange</h1>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-0.5">Espace Interne</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1.5">
            <span className="h-2 w-2 rounded-full bg-accent animate-pulse" />
            <span className="text-xs font-semibold" data-testid="countdown-petero">
              J-{joursAvantSpectacle()} · Petero Nzukira
            </span>
          </div>
        </div>
        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={lienClasse} data-testid={item.testId}>
              <item.Icone className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {NAV_SECONDAIRE.map((item) => (
            <NavLink key={item.to} to={item.to} className={lienClasse} data-testid={item.testId}>
              <item.Icone className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {NAV_COMMUNICATION.length > 0 && (
            <>
              <p className="px-4 pt-5 pb-1 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Communication
              </p>
              {NAV_COMMUNICATION.map((item) => (
                <NavLink key={item.to} to={item.to} className={lienClasse} data-testid={item.testId}>
                  <item.Icone className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
          {estAdmin && (
            <>
              <p className="px-4 pt-5 pb-1 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Administration
              </p>
              {NAV_ADMIN.map((item) => (
                <NavLink key={item.to} to={item.to} className={lienClasse} data-testid={item.testId}>
                  <item.Icone className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="lg" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{ROLES[user?.role] || user?.role}</p>
            </div>
            <ThemeToggle />
            <button
              onClick={deconnexion}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Se déconnecter"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ——— En-tête mobile ——— */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-card/95 backdrop-blur border-b border-border px-5 py-3">
        <div>
          <h1 className="font-serif text-xl font-bold leading-none">Inyange</h1>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Espace Interne</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold rounded-full bg-accent/20 px-2.5 py-1">
            J-{joursAvantSpectacle()}
          </span>
          <ThemeToggle />
          <UserAvatar user={user} />
        </div>
      </header>

      {/* ——— Contenu ——— */}
      <main className="md:pl-64 pb-24 md:pb-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-6 md:pt-10">
          <Outlet />
        </div>
      </main>

      {/* ——— Navigation basse mobile ——— */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border flex justify-around px-2 py-1.5" data-testid="mobile-bottom-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[10px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <item.Icone className="h-5 w-5" />
            {item.labelCourt || item.label}
          </NavLink>
        ))}
        <Sheet open={menuOuvert} onOpenChange={setMenuOuvert}>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center gap-0.5 px-3 py-1.5 text-[10px] font-medium text-muted-foreground" data-testid="mobile-menu-plus">
              <MoreHorizontal className="h-5 w-5" />
              Plus
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <SheetTitle className="font-serif text-xl mb-4">Menu</SheetTitle>
            <div className="space-y-1.5 pb-4">
              {[...NAV_SECONDAIRE, ...NAV_COMMUNICATION].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOuvert(false)}
                  className={lienClasse}
                >
                  <item.Icone className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
              {estAdmin &&
                NAV_ADMIN.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMenuOuvert(false)}
                    className={lienClasse}
                  >
                    <item.Icone className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              <button
                onClick={deconnexion}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-destructive hover:bg-muted transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Se déconnecter
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
